import { useEffect, useState } from 'react';
import { api, errMsg } from '../../lib/api';

interface Memorial {
  id: string;
  slug: string;
  deceasedName: string;
  memorialType: string;
  visibility: string;
  tributeCount: number;
  viewCount: number;
  status: string;
  createdAt: number;
}

interface Order {
  id: string;
  memorialId: string;
  totalCents: number;
  currency: string;
  status: string;
  provider: string;
  createdAt: number;
  paidAt: number | null;
}

type Tab = 'memorials' | 'orders';

export default function AdminData() {
  const [tab, setTab] = useState<Tab>('memorials');

  return (
    <div>
      <div className="mb-5 flex gap-2">
        {[
          { id: 'memorials' as Tab, label: '纪念馆' },
          { id: 'orders' as Tab, label: '订单流水' },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`rounded-full px-4 py-1.5 text-xs transition ${
              tab === t.id ? 'bg-ink-900 text-ink-50' : 'bg-ink-100 text-ink-600'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'memorials' ? <MemorialList /> : <OrderList />}
    </div>
  );
}

function MemorialList() {
  const [items, setItems] = useState<Memorial[]>([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    const res = await api<{ items: Memorial[] }>('/admin/memorials');
    if (res.ok && res.data) setItems(res.data.items);
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  async function toggle(m: Memorial) {
    await api(`/admin/memorials/${m.id}/toggle`, { method: 'POST', body: '{}' });
    load();
  }

  if (loading) return <p className="text-sm text-ink-500">加载中…</p>;

  return (
    <div className="overflow-x-auto rounded-2xl border border-ink-200 bg-white">
      <table className="w-full text-sm">
        <thead className="border-b border-ink-100 bg-ink-50/60 text-left text-xs text-ink-600">
          <tr>
            <th className="px-4 py-3">逝者</th>
            <th className="px-4 py-3">类型</th>
            <th className="px-4 py-3">隐私</th>
            <th className="px-4 py-3">祭品</th>
            <th className="px-4 py-3">浏览</th>
            <th className="px-4 py-3">状态</th>
            <th className="px-4 py-3 text-right">操作</th>
          </tr>
        </thead>
        <tbody>
          {items.map((m) => (
            <tr key={m.id} className="border-b border-ink-50 last:border-0">
              <td className="px-4 py-3 text-ink-900">{m.deceasedName}</td>
              <td className="px-4 py-3 text-xs text-ink-600">
                {m.memorialType === 'hero' ? '英雄纪念堂' : '亲友纪念馆'}
              </td>
              <td className="px-4 py-3 text-xs text-ink-500">{m.visibility}</td>
              <td className="px-4 py-3 text-ink-700">{m.tributeCount}</td>
              <td className="px-4 py-3 text-ink-500">{m.viewCount}</td>
              <td className="px-4 py-3">
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] ${
                    m.status === 'active'
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-ink-100 text-ink-500'
                  }`}
                >
                  {m.status === 'active' ? '正常' : '已归档'}
                </span>
              </td>
              <td className="px-4 py-3 text-right">
                <button
                  onClick={() => toggle(m)}
                  className={`text-xs underline ${
                    m.status === 'active' ? 'text-red-600' : 'text-emerald-700'
                  }`}
                >
                  {m.status === 'active' ? '归档' : '恢复'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OrderList() {
  const [items, setItems] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api<{ items: Order[] }>('/admin/orders').then((res) => {
      if (res.ok && res.data) setItems(res.data.items);
      else setError(errMsg(res, '加载失败'));
      setLoading(false);
    });
  }, []);

  if (loading) return <p className="text-sm text-ink-500">加载中…</p>;
  if (error) return <p className="text-sm text-red-600">{error}</p>;

  const fmt = (ts: number | null) =>
    ts ? new Date(ts * 1000).toLocaleString('zh-CN') : '—';

  return (
    <div className="overflow-x-auto rounded-2xl border border-ink-200 bg-white">
      <table className="w-full text-sm">
        <thead className="border-b border-ink-100 bg-ink-50/60 text-left text-xs text-ink-600">
          <tr>
            <th className="px-4 py-3">订单号</th>
            <th className="px-4 py-3">金额</th>
            <th className="px-4 py-3">渠道</th>
            <th className="px-4 py-3">状态</th>
            <th className="px-4 py-3">创建时间</th>
            <th className="px-4 py-3">支付时间</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr>
              <td colSpan={6} className="px-4 py-8 text-center text-sm text-ink-400">
                暂无订单
              </td>
            </tr>
          )}
          {items.map((o) => (
            <tr key={o.id} className="border-b border-ink-50 last:border-0">
              <td className="px-4 py-3 font-mono text-xs text-ink-600">{o.id.slice(0, 12)}…</td>
              <td className="px-4 py-3 text-ink-900">
                ${(o.totalCents / 100).toFixed(2)} {o.currency}
              </td>
              <td className="px-4 py-3 text-xs text-ink-500">{o.provider}</td>
              <td className="px-4 py-3">
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] ${
                    o.status === 'paid'
                      ? 'bg-emerald-50 text-emerald-700'
                      : o.status === 'pending'
                        ? 'bg-amber-50 text-amber-700'
                        : 'bg-ink-100 text-ink-500'
                  }`}
                >
                  {o.status}
                </span>
              </td>
              <td className="px-4 py-3 text-xs text-ink-500">{fmt(o.createdAt)}</td>
              <td className="px-4 py-3 text-xs text-ink-500">{fmt(o.paidAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
